/* ============================================================================
   WORKFLOW ↔ ROBOT ↔ FORMULA CARD — peraturan tulen (tiada Firebase) · 3 Nov 2026
   ============================================================================
   Keputusan user 3 Nov ("Cara A"): dropdown Workflow dalam Control Panel tab Flow (Workspace v4
   mod New) membaca senarai page /workflow, dan memilih SENDIRI workflow ikut Formula card yang
   ditick. Pelanggan hanya boleh menukar antara workflow yang BOLEH dijalankan untuk card itu.

   Setiap card dalam page /workflow menyimpan medan `robot` (pilih dalam popup edit). Itu sambungan
   page → robot. Card tanpa robot = tidak muncul dalam dropdown (tiada kawalan palsu).
     · `robot` tiada langsung (undefined) → guna ROBOT_LALAI ikut id senarai permulaan (13 workflow
       sedia ada terus bersambung tanpa admin perlu Save satu-satu).
     · `robot: ''` → admin sengaja "Not linked".

   ⚠️ KEMBAR jenis card: `jenisKadWorkflow` membaca `getEffectiveScriptKey` (config/modules.js) —
      fungsi yang SAMA dipakai pagar `kadTakSokong` + `productsToRun` dalam WorkspaceView. Jangan
      salin logiknya ke sini; ia disuntik sebagai parameter supaya fail ini kekal tulen.
   ⛔ Peraturan BLANK CARD duduk dalam utils/workflowBlankCard.js (agent lain sedang membina laluan
      Run blank card — fail berasingan supaya tiada suntingan bertembung).
   ========================================================================== */
import { robotUntukBlankCard } from './workflowBlankCard.js';

/* Robot yang wujud hari ini (Flow). `cara` = bagaimana dropdown menggerakkan larian:
     'flowWorkflow' → menetapkan nilai Control Panel sedia ada (generate | kad) — robot AI8SAAT sahaja
     'formula'      → robot ditentukan Formula card; dropdown hanya memapar (tiada pilihan lain) */
export const ROBOT_WF = [
    { id: 'ai8saat', label: 'AI8SAAT — AI image to video, 1 scene', cara: 'flowWorkflow', flowWorkflow: 'generate' },
    { id: 'ai8saat-kad', label: 'AI8SAAT — card images to video', cara: 'flowWorkflow', flowWorkflow: 'kad' },
    { id: 'ai16saat', label: 'AI16SAAT — 2 scenes', cara: 'formula' },
    { id: 'goyang', label: 'GOYANG — motion template', cara: 'formula' },
    { id: 'nscene', label: 'FLOWNITY — new image every scene', cara: 'formula' },
    { id: 'resipi-sama', label: 'Resipi — one image, many scenes', cara: 'formula' },
    { id: 'resipi-berlainan', label: 'Resipi — new image every scene', cara: 'formula' },
    { id: 'resipi-tiada', label: 'Resipi — text to video', cara: 'formula' },
    { id: 'resipi-frame', label: 'Resipi — before and after', cara: 'formula' },
    { id: 'resipi-sahaja', label: 'Resipi — images only', cara: 'formula' },
];
export const ID_ROBOT_WF = new Set(ROBOT_WF.map((r) => r.id));
export const robotWfIkutId = (id) => ROBOT_WF.find((r) => r.id === id) || null;

/* Sambungan lalai untuk 13 workflow senarai permulaan (utils/galeriWorkflow.js WORKFLOW_MULA).
   Yang belum dibina (versi · extend · semak · ulang) sengaja TIADA → tidak muncul dalam dropdown. */
export const ROBOT_LALAI = {
    'wf-1scene': 'ai8saat',
    'wf-kad': 'ai8saat-kad',
    'wf-2scene': 'ai16saat',
    'wf-template': 'goyang',
    'wf-berlainan': 'nscene',
    'wf-sama': 'resipi-sama',
    'wf-teks': 'resipi-tiada',
    'wf-before-after': 'resipi-frame',
    'wf-gambar': 'resipi-sahaja',
};

/** Robot sebuah workflow page: medan sendiri dahulu, lalai ikut id kemudian. '' = tiada. */
export function robotWorkflow(w) {
    if (!w || typeof w !== 'object') return '';
    if (typeof w.robot === 'string') return ID_ROBOT_WF.has(w.robot) ? w.robot : '';
    return ROBOT_LALAI[w.id] || '';
}

/**
 * Jenis card untuk tujuan workflow. Pulang id robot (cth 'ai8saat', 'resipi-sama'), 'blank', atau
 * 'lain:<scriptKey>' bila robotnya tiada dalam ROBOT_WF (cth Dinamik) — tetap dikira supaya card
 * bercampur dikesan, bukan dibuang senyap.
 * @param p produk/card
 * @param deps { getEffectiveScriptKey, getModule } daripada config/modules.js
 */
export function jenisKadWorkflow(p, { getEffectiveScriptKey, getModule }) {
    if (!p) return 'lain:?';
    if (p.kosong) return 'blank';
    const sk = getEffectiveScriptKey(p) || 'ai8saat';
    if (sk === 'resipi') {
        const g = getModule(p.moduleId)?.resipi?.gambar || 'sama';
        const id = `resipi-${g}`;
        return ID_ROBOT_WF.has(id) ? id : `lain:${id}`;
    }
    return ID_ROBOT_WF.has(sk) ? sk : `lain:${sk}`;
}

/** Robot yang boleh dipilih untuk satu jenis card. */
export function robotSerasi(jenis) {
    if (jenis === 'blank') return robotUntukBlankCard();
    if (jenis === 'ai8saat') return ['ai8saat', 'ai8saat-kad'];
    return ID_ROBOT_WF.has(jenis) ? [jenis] : [];
}

/**
 * Ringkas card yang ditick → [{ jenis, label, bil }] (susunan ikut kemunculan pertama).
 * `label` = nama Formula untuk paparan (getDisplayLabelForProduct).
 */
export function ringkasKadDitanda(kad, { jenisKad, labelKad }) {
    const peta = new Map();
    for (const p of kad || []) {
        const jenis = jenisKad(p);
        const x = peta.get(jenis);
        if (x) x.bil += 1;
        else peta.set(jenis, { jenis, label: labelKad(p), bil: 1 });
    }
    return [...peta.values()];
}

/**
 * Keputusan dropdown.
 * @param senarai workflow page (sudah disemak + disusun)
 * @param ringkasan hasil ringkasKadDitanda
 * @param flowWorkflow nilai Control Panel semasa ('generate' | 'kad')
 * @param tunjukDraft admin = true
 * @param robotBlank pilihan dropdown untuk blank card (state paparan; lihat workflowBlankCard.js)
 * @returns {{ keadaan: 'tiada'|'campur'|'satu', pilihan: object[], nilai: string, jenis?: string, ringkasan }}
 *   pilihan = workflow page (satu bagi setiap robot — pendua robot dilangkau), nilai = id workflow terpilih.
 */
export function keputusanDropdown({ senarai = [], ringkasan = [], flowWorkflow = 'generate', tunjukDraft = false, robotBlank = '' }) {
    if (!ringkasan.length) return { keadaan: 'tiada', pilihan: [], nilai: '', ringkasan };
    if (ringkasan.length > 1) return { keadaan: 'campur', pilihan: [], nilai: '', ringkasan };

    const { jenis } = ringkasan[0];
    const serasi = robotSerasi(jenis);
    const dilihat = new Set();
    const pilihan = [];
    for (const w of senarai) {
        if (!tunjukDraft && w.status !== 'published') continue;
        const r = robotWorkflow(w);
        if (!r || !serasi.includes(r) || dilihat.has(r)) continue;
        dilihat.add(r);
        pilihan.push({ ...w, robot: r });
    }

    let robotDipilih;
    if (jenis === 'blank') robotDipilih = serasi.includes(robotBlank) ? robotBlank : (flowWorkflow === 'kad' ? 'ai8saat-kad' : 'ai8saat');
    else if (jenis === 'ai8saat') robotDipilih = flowWorkflow === 'kad' ? 'ai8saat-kad' : 'ai8saat';
    else robotDipilih = serasi[0];

    const kini = pilihan.find((w) => w.robot === robotDipilih) || null;
    return { keadaan: 'satu', pilihan, nilai: kini ? kini.id : '', jenis, ringkasan };
}
